import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  ConfirmModal,
  Modal,
  ModalFooter,
  Switch,
} from "@/components/ui";
import { getApiErrorMessage } from "@/lib/errorMessages";
import { isReaderPaired } from "@/lib/readerLabel";
import {
  formatReaderSerialNumber,
  readerSerialMatchesQuery,
} from "@/lib/readerSerialNumber";
import { deviceKeys, deviceService } from "@/services/deviceService";
import { readerKeys, readerService } from "@/services/readerService";
import type { Device, PaymentReader } from "@/types";
import { ReaderStatus } from "@/types";
import { ClickToCopyId, PickerPanel } from "@/components/common";
import { DetailSectionTitle } from "@/components/ui";

interface DevicePosConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: Device | null;
  /** When set, scopes device reader APIs with `house_id` (device must belong to that house). */
  houseId?: string;
  onUpdated?: () => void | Promise<void>;
}

function isMockReader(reader: PaymentReader): boolean {
  return reader.provider.toUpperCase() === "MOCK";
}

function isLinkableReader(reader: PaymentReader, device: Device): boolean {
  if (isMockReader(reader)) {
    return true;
  }
  if (reader.provider.toUpperCase() !== "SUMUP") {
    return false;
  }
  if (reader.status !== ReaderStatus.PAIRED) {
    return false;
  }
  if (device.region_id && reader.region_id !== device.region_id) {
    return false;
  }
  return true;
}

export function DevicePosConfigModal({
  isOpen,
  onClose,
  device,
  houseId,
  onUpdated,
}: DevicePosConfigModalProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("kiosk");
  const queryClient = useQueryClient();
  const [serialSearch, setSerialSearch] = useState("");
  const [showMockReaders, setShowMockReaders] = useState(false);
  const [selectedReader, setSelectedReader] = useState<PaymentReader | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
  const linkedReaderId = device?.reader?.id;

  useEffect(() => {
    if (!isOpen || !device) {
      return;
    }
    setSerialSearch("");
    setShowMockReaders(false);
    setSelectedReader(null);
    setError(null);
    setUnlinkConfirmOpen(false);
  }, [isOpen, device]);

  const availableReadersParams = { available_only: true } as const;
  const {
    data: availableReadersRaw = [],
    isFetching: loadingReaders,
    isError: availableReadersError,
    error: availableReadersQueryError,
  } = useQuery({
    queryKey: readerKeys.list(availableReadersParams),
    queryFn: () => readerService.getAll(availableReadersParams),
    enabled: isOpen && !!device,
  });

  const availableReaders = useMemo(() => {
    if (!device) return [];
    return availableReadersRaw.filter((reader) =>
      isLinkableReader(reader, device),
    );
  }, [availableReadersRaw, device]);

  const readersLoadError = availableReadersError
    ? getApiErrorMessage(availableReadersQueryError, "loadReaders")
    : null;
  const displayError = error ?? readersLoadError;

  const { data: linkedReaderDetails = null } = useQuery({
    queryKey: readerKeys.detail(linkedReaderId ?? ""),
    queryFn: () => readerService.getById(linkedReaderId!),
    enabled: isOpen && !!linkedReaderId,
  });

  const filteredReaders = useMemo(() => {
    const visible = showMockReaders
      ? availableReaders
      : availableReaders.filter((reader) => !isMockReader(reader));
    const query = serialSearch.trim().toLowerCase();
    if (!query) {
      return visible;
    }
    return visible.filter((reader) =>
      readerSerialMatchesQuery(reader.serial_number, query),
    );
  }, [availableReaders, serialSearch, showMockReaders]);

  const reader = device?.reader;
  const linkedReaderPaired = isReaderPaired(reader);
  const linkedReaderSerial = linkedReaderDetails
    ? formatReaderSerialNumber(
        linkedReaderDetails.serial_number,
        linkedReaderDetails.provider,
      )
    : reader?.serial_number;
  const canRefreshLinkedReader =
    linkedReaderDetails !== null && !isMockReader(linkedReaderDetails);
  const selectedIsMock =
    selectedReader != null && isMockReader(selectedReader);

  useEffect(() => {
    if (selectedReader && isMockReader(selectedReader) && !showMockReaders) {
      setSelectedReader(null);
    }
  }, [selectedReader, showMockReaders]);

  const invalidateAfterReaderMutation = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: readerKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: readerKeys.details() }),
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() }),
      houseId
        ? queryClient.invalidateQueries({
            queryKey: deviceKeys.byHouse(houseId),
          })
        : Promise.resolve(),
    ]);
  };

  const run = async (fn: () => Promise<unknown>): Promise<boolean> => {
    setError(null);
    setBusy(true);
    try {
      await fn();
      await invalidateAfterReaderMutation();
      await onUpdated?.();
      return true;
    } catch (err) {
      setError(getApiErrorMessage(err, "readerOperation"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const linkSelectedReader = () => {
    if (!device || !selectedReader) {
      setError(t("posConfig.selectReaderRequired"));
      return;
    }

    const link = selectedIsMock
      ? () =>
          deviceService.pairMockReader(
            device.id,
            { reader_id: selectedReader.id },
            houseId,
          )
      : () =>
          deviceService.linkReader(
            device.id,
            { reader_id: selectedReader.id },
            houseId,
          );

    void run(link).then((ok) => {
      if (ok) onClose();
    });
  };

  const handleRefresh = () => {
    if (!device) return;
    void run(async () => {
      await deviceService.refreshSumupReader(device.id, houseId);
    });
  };

  const confirmUnlink = () => {
    if (!device) return;
    setUnlinkConfirmOpen(false);
    void run(async () => {
      await deviceService.unlinkReader(device.id, houseId);
    });
  };

  return (
    <>
      <Modal
        closeLabel={tCommon("close")}
        isOpen={isOpen && device != null}
        onClose={onClose}
        title={t("posConfig.title")}
        description={device?.display_name}
        size="md"
        footer={
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={busy}
            >
              {tCommon("close")}
            </Button>
            <Button
              type="button"
              disabled={busy || !selectedReader}
              isLoading={busy}
              onClick={linkSelectedReader}
            >
              {reader && linkedReaderPaired
                ? t("posConfig.linkReplace")
                : selectedIsMock
                  ? t("posConfig.linkMockReader")
                  : t("posConfig.linkReader")}
            </Button>
          </ModalFooter>
        }
      >
        {device ? (
          <div className="space-y-5">
            {reader && linkedReaderPaired ? (
              <section>
                <DetailSectionTitle as="h3">
                  {t("posConfig.linkedReaderTitle")}
                </DetailSectionTitle>
                <div className="mt-2 rounded-lg border border-surface-200 bg-surface-50 px-4 py-3">
                  {linkedReaderSerial && reader ? (
                    <ClickToCopyId
                      value={
                        linkedReaderDetails?.serial_number ??
                        reader.serial_number
                      }
                      displayValue={linkedReaderSerial}
                      ellipsis="start"
                    />
                  ) : null}
                  {linkedReaderDetails ? (
                    <>
                      <p className="mt-1.5 text-sm text-surface-700">
                        {linkedReaderDetails.brand} {linkedReaderDetails.model}
                      </p>
                      <p className="mt-1 text-xs text-surface-500">
                        {[
                          linkedReaderDetails.provider,
                          linkedReaderDetails.region_code ||
                            linkedReaderDetails.flow,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      <div className="mt-2 break-all text-xs text-surface-400">
                        {t("posConfig.readerId")}{" "}
                        <ClickToCopyId
                          value={
                            linkedReaderDetails.external_reader_id ?? reader.id
                          }
                        />
                      </div>
                    </>
                  ) : (
                    <div className="mt-2 break-all text-xs text-surface-400">
                      {t("posConfig.readerId")}{" "}
                      <ClickToCopyId value={reader.id} />
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {canRefreshLinkedReader ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={handleRefresh}
                      >
                        {t("posConfig.refreshFromSumup")}
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-error-700"
                      disabled={busy}
                      onClick={() => setUnlinkConfirmOpen(true)}
                    >
                      {t("posConfig.disconnect")}
                    </Button>
                  </div>
                </div>
              </section>
            ) : null}

            <section className="space-y-3">
              {reader && linkedReaderPaired ? (
                <DetailSectionTitle as="h3">
                  {t("posConfig.replaceWithNewPairing")}
                </DetailSectionTitle>
              ) : null}

              <PickerPanel
                searchPlaceholder={t("posConfig.searchSerialPlaceholder")}
                searchValue={serialSearch}
                onSearchChange={setSerialSearch}
                searchDisabled={busy}
                searchAccessory={
                  <Switch
                    checked={showMockReaders}
                    onChange={(event) =>
                      setShowMockReaders(event.target.checked)
                    }
                    disabled={busy}
                    label={t("posConfig.showMockReaders")}
                  />
                }
                isLoading={loadingReaders}
                listVariant="divided"
                emptyMessage={t("posConfig.noAvailableReaders")}
                items={filteredReaders.map((item) => ({
                  id: item.id,
                  title: formatReaderSerialNumber(
                    item.serial_number,
                    item.provider,
                  ),
                  subtitle: `${item.brand} ${item.model}`,
                  trailing: item.provider,
                  trailingDetail: item.region_code ?? item.flow,
                  selected: selectedReader?.id === item.id,
                  onSelect: () => setSelectedReader(item),
                }))}
              />

              {displayError ? (
                <Alert variant="error">{displayError}</Alert>
              ) : null}
            </section>
          </div>
        ) : null}
      </Modal>
      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
       
        isOpen={unlinkConfirmOpen}
        onClose={() => setUnlinkConfirmOpen(false)}
        onConfirm={confirmUnlink}
        title={t("posConfig.disconnect")}
        message={t("posConfig.unlinkConfirm")}
        confirmText={t("posConfig.disconnect")}
        isDestructive
        isLoading={busy}
      />
    </>
  );
}
